/**
 * Scene setup utilities
 * Handles post-render setup for scenes
 */

import { setupStaticImages, setupSparkles, setupClickReveal } from '../animations'
import type { SceneData, SceneConfig } from '../types'

export interface SceneSetupContext {
  data: SceneData
  config: SceneConfig
  container: HTMLDivElement
  imageContainer: HTMLDivElement | null
  clickedActionPointIndices: Set<number>
  onApplySpacing: () => void
  onSetupSpacingResizeListener: () => void
  onSetupActionPoints: () => void
  onSetupScrollLock: () => void
  onSetupAnimations: () => void
  onSetupSceneAnimation: () => void
  onSetupScrollIntoCenterSound: (element: HTMLElement, sceneId: string, fileName: string, options?: { optional?: boolean; trigger?: { start: { element: number; viewport: number } } }) => void
}

/**
 * Setup scene after image loads
 */
export function setupSceneAfterImageLoad(context: SceneSetupContext): void {
  if (context.data.padding || context.data.margin) {
    requestAnimationFrame(() => {
      context.onApplySpacing()
      context.onSetupSpacingResizeListener()
    })
  }

  if (context.data.speechBubbles && context.data.speechBubbles.length > 0 && context.data.type !== 'chapter-title') {
    context.onSetupActionPoints()
    context.onSetupScrollLock()
  }

  if (context.data.animations && context.data.animations.length > 0) {
    context.onSetupAnimations()
  }

  // Setup scene-level animation if configured
  if (context.data.animation) {
    context.onSetupSceneAnimation()
  }

  // Setup static images if configured
  if (context.data.images && context.data.images.length > 0) {
    setupStaticImages({
      data: context.data,
      config: context.config,
      container: context.container,
      imageContainer: context.imageContainer,
    })
  }

  if (context.data.sparkles && context.data.sparkles.length > 0) {
    setupSparkles({
      data: context.data,
      config: context.config,
      container: context.container,
      imageContainer: context.imageContainer,
    })
  }

  if (context.data.clickReveal) {
    setupClickReveal({
      data: context.data,
      config: context.config,
      container: context.container,
      imageContainer: context.imageContainer,
    })
  }

  // Play scene sounds declared in audio.sounds[]
  if (context.data.audio?.sounds) {
    for (const sound of context.data.audio.sounds) {
      context.onSetupScrollIntoCenterSound(context.container, context.data.id, sound.file, {
        optional: true,
        trigger: sound.trigger,
      })
    }
  }
}
